import React, { useReducer, Dispatch } from "react";

import Modal, { MODAL_SIZE } from "components/shared/modal";
import Button, { ButtonColor } from "components/shared/button";
import CheckboxWithLabel from "components/shared/checkbox_with_label";
import StyledError from "components/shared/error";
import StyledScrollBox from "components/shared/scroll_box";

import {
  posUsersFromPeopleWithShifts,
  NotifyType,
} from "helpers/schedule/scheduling_page";
import {
  initPosUserState,
  reducer,
  getSelectedUUIds,
  IPosUserUUIDStatus,
  IPosUserUUIDState,
} from "helpers/schedule/notify_modal";
import styled from "styles/styled_components";
import { RequestStatus } from "types/requests";
import { IPeople } from "types/schedules";

const StyledTable = styled.table`
  font-size: ${props => props.theme.text.size.small};
  margin-bottom: ${props => props.theme.spacing.large};
  margin-left: 5%;
  text-align: left;
  table-layout: auto;
  width: 90%;
`;

const StyledNotice = styled.div`
  text-align: left;
  margin-bottom: ${props => props.theme.spacing.large};
`;

const StyledTableRow = styled.tr``;

const StyledTableColumn = styled.td`
  border: ${props => props.theme.lightBorder};
  font-size: ${props => props.theme.text.size.base};
  padding: ${props => `${props.theme.spacing.small} 0`};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: ${props => `calc(22 * ${props.theme.gridBase})`};
`;

const StyledTableHeader = styled.th`
  background-color: ${props => props.theme.colors.bgDark};
  color: ${props => props.theme.colors.brandGray40};
  font-family: ${props => props.theme.text.mediumFamily};
  padding-bottom: ${props => props.theme.spacing.medium};
  position: sticky;
  top: 0;
  z-index: 1;
`;

interface IContactDisplay {
  notAvailable?: boolean;
}

const StyledContactDisplay = styled.span<IContactDisplay>`
  color: ${props =>
    props.notAvailable ? props.theme.colors.brandGray30 : "inherit"};
`;

interface INotifyModal {
  onCancel: () => void;
  onSend: (notifyType: NotifyType, selectedUUIds: string[]) => void;
  people: IPeople;
  publishedLinkAddress: string;
  publishSchedulePostRequestStatus: RequestStatus;
}

type IRequestStatusMessages = { [key in RequestStatus]: string | JSX.Element };
const REQUEST_STATUS_MESSAGES: IRequestStatusMessages = {
  [RequestStatus.REQUESTING]: "Publishing schedule...",
  [RequestStatus.SUCCESS]: "Your schedule has been published.",
  [RequestStatus.FAILURE]: (
    <StyledError>
      Your schedule failed to publish. Please try again.
    </StyledError>
  ),
  [RequestStatus.NOT_STARTED]: "Your schedule is not published.",
};

export default function NotifyModal({
  onCancel,
  onSend,
  people,
  publishSchedulePostRequestStatus,
  publishedLinkAddress,
}: INotifyModal) {
  const posUsers = posUsersFromPeopleWithShifts(people);
  const [state, dispatch]: [IPosUserUUIDState, Dispatch<string>] = useReducer(
    reducer,
    posUsers,
    initPosUserState
  );
  const employeeRows = posUsers.map(posUser => {
    const { firstName, lastName, uuid } = posUser;
    return (
      <StyledTableRow key={uuid}>
        <StyledTableColumn>
          <CheckboxWithLabel
            checked={state.selection[uuid] === IPosUserUUIDStatus.SELECTED}
            disabled={state.selection[uuid] === IPosUserUUIDStatus.DISABLED}
            label={`${firstName} ${lastName}`}
            onChangeHandler={() => dispatch(uuid)}
          />
        </StyledTableColumn>
        <StyledTableColumn>
          {posUser.email.match({
            Just: email => <StyledContactDisplay>{email}</StyledContactDisplay>,
            Nothing: () => (
              <StyledContactDisplay notAvailable>
                not available
              </StyledContactDisplay>
            ),
          })}
        </StyledTableColumn>
        <StyledTableColumn>
          {posUser.phone.match({
            Just: phone => <StyledContactDisplay>{phone}</StyledContactDisplay>,
            Nothing: () => (
              <StyledContactDisplay notAvailable>
                not available
              </StyledContactDisplay>
            ),
          })}
        </StyledTableColumn>
      </StyledTableRow>
    );
  });
  const requestStatusMessage =
    REQUEST_STATUS_MESSAGES[publishSchedulePostRequestStatus];
  const isRequestSuccess =
    publishSchedulePostRequestStatus === RequestStatus.SUCCESS;
  const previewLink = publishedLinkAddress && isRequestSuccess && (
    <a href={`/${publishedLinkAddress}`} target="_blank">
      Preview schedule
    </a>
  );

  const { canEmail, canSms, uuidsWithEmail, uuidsWithPhone } = state;
  let error;
  if (!(uuidsWithEmail.size || uuidsWithPhone.size)) {
    // There are no users available to notify, display an error.
    error = (
      <StyledError>
        You must have at least one employee with an email or phone to notify
        employees.
      </StyledError>
    );
  } else if (!(canEmail || canSms)) {
    // Even if recipients are available, you must highlight someone to notify.
    error = (
      <StyledError>
        Please select at least one employee with an email or phone to notify
        them.
      </StyledError>
    );
  }
  return (
    <Modal title="Notify employees" onClose={onCancel} size={MODAL_SIZE.LARGE}>
      <StyledNotice>
        {requestStatusMessage} {previewLink}
      </StyledNotice>
      <StyledScrollBox>
        <StyledTable>
          <thead>
            <StyledTableRow>
              <StyledTableHeader>EMPLOYEE</StyledTableHeader>
              <StyledTableHeader>EMAIL</StyledTableHeader>
              <StyledTableHeader>PHONE</StyledTableHeader>
            </StyledTableRow>
          </thead>
          <tbody>{employeeRows}</tbody>
        </StyledTable>
      </StyledScrollBox>
      {error}
      <Button
        buttonColor={ButtonColor.GRAY}
        disabled={!(isRequestSuccess && canEmail)}
        label="Send Email"
        onClick={() => onSend(NotifyType.EMAIL_ONLY, getSelectedUUIds(state))}
      />
      <Button
        buttonColor={ButtonColor.GRAY}
        disabled={!(isRequestSuccess && canSms)}
        label="Send SMS"
        onClick={() => onSend(NotifyType.SMS_ONLY, getSelectedUUIds(state))}
      />
      <Button
        buttonColor={ButtonColor.BLUE}
        disabled={!(isRequestSuccess && canEmail && canSms)}
        label="Send Email &amp; SMS"
        onClick={() =>
          onSend(NotifyType.SMS_AND_EMAIL, getSelectedUUIds(state))
        }
      />
    </Modal>
  );
}
